import { BarChartOutlined, FileTextOutlined } from '@ant-design/icons';
import { getTableInfo } from '../api/projects';

// 新增一個 mappingTable 快取
let mappingTableCache = null;

// 使用純數據結構定義 projectRoutes，不包含 JSX 元素
export const projectRoutes = [
  {
    label: '儀表板',
    key: 'dashboard',
    icon: <BarChartOutlined />,
    children: []
  },
];

// 初始化 projectRoutes 的函數，使用純數據結構
export const initProjectRoutes = async () => {
  try {
    if (!mappingTableCache) {
      const response = await getTableInfo();
      mappingTableCache = response.data;
    }

    if (mappingTableCache && mappingTableCache.length > 0) {
      // 按照 sort_id 排序所有項目
      const sortedItems = [...mappingTableCache].sort((a, b) => (a.sort_id || 0) - (b.sort_id || 0));
      
      // 創建一個物件來儲存所有「主類別」及其子項目
      const categoriesMap = {};
      
      // 第一步：收集所有不同的「主類別」(categories[0])，並建立基本結構
      sortedItems.forEach(item => {
        const categories = Array.isArray(item['類別']) ? item['類別'] : [item['類別']];
        if (categories.length > 0) {
          const mainCategory = categories[0];
          if (!categoriesMap[mainCategory]) {
            categoriesMap[mainCategory] = {
              label: mainCategory,
              key: `category-${mainCategory}`,
              icon: mainCategory.includes('儀表板') ? <BarChartOutlined /> : <FileTextOutlined />,
              children: [],
              isCategoryParent: true // 標記為類別父項
            };
          }
        }
      });

      // 第二步：逐筆處理，將資料掛載到對應的主類別或子類別上
      sortedItems.forEach(item => {
        const categories = Array.isArray(item['類別']) ? item['類別'] : [item['類別']];
        if (categories.length > 0) {
          const mainCategory = categories[0];
          const displayName = item.display_name || item.table_name;

          // 建立當前項目的資訊
          const itemInfo = {
            label: displayName,
            key: item.table_name || `item-${item.primaryViewId}`,
            path: `/project/management/${displayName}`,
            viewId: item.primaryViewId,
            isGrafana: item.來源 === 'Grafana',
            grafana_url: item.grafana_url || ''
          };

          // 偵測是否「顯示名稱」與「主類別」相同，若相同代表該項目即是主類別本身
          const isMainCategoryParent = (displayName === mainCategory);

          // 只有一層類別時
          if (categories.length === 1) {
            // 若這個項目就是主類別本身，更新它；否則直接作為子項目
            if (isMainCategoryParent) {
              const existingChildren = categoriesMap[mainCategory].children || [];
              categoriesMap[mainCategory] = {
                ...itemInfo,
                icon: categoriesMap[mainCategory].icon,
                children: existingChildren,
                isCategoryParent: true,
                key: `category-${mainCategory}`
              };
            } else {
              categoriesMap[mainCategory].children.push(itemInfo);
            }
          } 
          // 兩層類別時
          else {
            const subCategory = categories[1];
            const isSubCategoryParent = (displayName === subCategory);

            // 檢查是否已有直接加入的子項(來自單一分類處理)與這個 subCategory 名稱相同
            let directChildIndex = categoriesMap[mainCategory].children.findIndex(child => !child.children && child.label === subCategory);
            let subCategoryNode = categoriesMap[mainCategory].children.find(child => child.label === subCategory && child.children);

            if (directChildIndex > -1) {
              // 若存在直接子項，先移除它，並以此建立或合併到 subCategoryNode
              const directChild = categoriesMap[mainCategory].children.splice(directChildIndex, 1)[0];
              if (!subCategoryNode) {
                subCategoryNode = {
                  label: subCategory,
                  key: directChild.key || `subcategory-${subCategory}`,
                  path: directChild.path,
                  viewId: directChild.viewId,
                  children: []
                };
                categoriesMap[mainCategory].children.push(subCategoryNode);
              }
            }

            if (!subCategoryNode) {
              // 尚未建立該子類別，則建立新的子分類節點
              subCategoryNode = {
                label: subCategory,
                key: `subcategory-${subCategory}`,
                children: []
              };
              categoriesMap[mainCategory].children.push(subCategoryNode);
            }

            if (isSubCategoryParent) {
              // 若當前項目即為子分類本身，則更新子分類節點(點擊後的路由)
              const existingSubChildren = subCategoryNode.children || [];
              subCategoryNode.label = displayName;
              subCategoryNode.key = item.table_name || `item-${item.primaryViewId}`;
              subCategoryNode.path = `/project/management/${displayName}`;
              subCategoryNode.viewId = item.primaryViewId;
              subCategoryNode.isGrafana = (item.來源 === 'Grafana');
              subCategoryNode.grafana_url = item.grafana_url || '';
              subCategoryNode.children = existingSubChildren;
            } else {
              // 否則，該 item 為子分類下的子項，加入子分類節點的 children
              // 構建完整的路徑，包含所有父層級
              const fullPath = `/project/management/${categories.slice(1).join('/')}/${displayName}`;
              itemInfo.path = fullPath;
              subCategoryNode.children.push(itemInfo);
            }
          }
        }
      });
      
      // 將 categoriesMap 轉成陣列
      const newRoutes = Object.values(categoriesMap);
      
      // 將 '儀表板' 類別的項目整合到最上層的 dashboard 中
      const dashboardItems = newRoutes.find(route => route.key === 'category-儀表板');
      const dashboardRoute = projectRoutes.find(route => route.key === 'dashboard');

      if (dashboardItems && dashboardRoute) {
        dashboardRoute.children = dashboardItems.children;
        // 從主類別陣列中移除「儀表板」的那個物件，因為已經放到頂層了
        const filteredRoutes = newRoutes.filter(route => route.key !== 'category-儀表板');
        return [dashboardRoute, ...filteredRoutes];
      }
      
      // 若沒有 '儀表板' 分類，或空的情況，直接回傳所有分類
      return [...projectRoutes, ...newRoutes.filter(route => route.key !== 'category-儀表板')];
    }
    
    // 若沒有任何資料，或發生其他狀況
    return projectRoutes;
  } catch (error) {
    console.error('Failed to initialize project routes:', error);
    return projectRoutes;
  }
};

export const getAllProjectPaths = () => {
  const paths = ['/project'];
  
  const processRoutes = (routes) => {
    routes.forEach(item => {
      if (item.path) {
        paths.push(item.path);
      }
      if (item.children && item.children.length > 0) {
        processRoutes(item.children);
      }
    });
  };
  
  processRoutes(projectRoutes);
  return paths;
};

export const clearTableInfoCache = () => {
  mappingTableCache = null;
};
